import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  Link,
  Outlet,
} from 'react-router-dom';

import {
  getUserVirtualAccount,
  createPaymentLink,
} from '../../../hooks/local/userReducer';

import gear from '../../../assets/svg-icons/blackSetting.svg';
import dewImage from '../../../assets/svg-icons/dewImage.svg';
import arrow from '../../../assets/svg-icons/blueArrow.svg';
import close from '../../../assets/svg-icons/closeBrand.svg';

import Input from '../../../components/input';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import usePartnerNavigation from '../../../hooks/usePartnerNavigation';

import {
  Eye,
  EyeOff,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';

const Home = () => {
  document.title =
    'Client area - CloudVantage';

  const dispatch = useDispatch();

  const {
    generatePartnerUrl,
  } = usePartnerNavigation();

  const selectedProject = useSelector(
    (state) =>
      state.user.selectedProject,
  );

  const {
    virtualAccount = {},
  } = useSelector(
    (state) => state.user,
  );

  const currency = useSelector(
    (state) => state.user.currency,
  );

  // ============================================================
  // WALLET MODAL
  // ============================================================

  const [
    walletDetails,
    setWalletDetails,
  ] = useState(false);

  const [
    balanceVisible,
    setBalanceVisible,
  ] = useState(false);

  // ============================================================
  // EXISTING USD PAYMENT LINK
  // ============================================================

  const [
    paymentLink,
    setPaymentLink,
  ] = useState('');

  const [
    linkGenerated,
    setLinkGenerated,
  ] = useState(false);

  const [
    amountValue,
    setAmountValue,
  ] = useState('');

  // ============================================================
  // NEW SQUAD PAYMENT LINK
  // ============================================================

  const [
    squadAmount,
    setSquadAmount,
  ] = useState('');

  const [
    squadPaymentLoading,
    setSquadPaymentLoading,
  ] = useState(false);

  const [
    verificationLoading,
    setVerificationLoading,
  ] = useState(false);

  /*
   * Prevent duplicate verification calls
   * under React StrictMode.
   */
  const verificationStartedRef =
    useRef(false);

  // ============================================================
  // ACCOUNT DETAILS
  // ============================================================

  const walletBalance =
    virtualAccount
      ? currency === 'USD'
        ? virtualAccount.dollar_balance
        : virtualAccount.balance
      : null;

  const accountName =
    virtualAccount?.acct_name || null;

  const accountNumber =
    virtualAccount?.acct_num || null;

  const hasPermanentVirtualAccount =
    Boolean(accountNumber);

  // ============================================================
  // LOAD WALLET
  // ============================================================

  useEffect(() => {
    dispatch(
      getUserVirtualAccount(),
    );
  }, [dispatch]);

  // ============================================================
  // VERIFY SQUAD PAYMENT AFTER REDIRECT
  // ============================================================

  useEffect(() => {
    const verifyPendingSquadPayment =
      async () => {
        if (
          verificationStartedRef.current
        ) {
          return;
        }

        const queryParams =
          new URLSearchParams(
            window.location.search,
          );

        /*
         * Current backend redirect format:
         *
         * /client-area?reference=SQPAY_xxx
         *
         * Keep older parameter names as
         * fallbacks for compatibility.
         */
        const transactionRef =
          queryParams.get('reference') ||
          queryParams.get('tref') ||
          queryParams.get(
            'transaction_ref',
          );

        /*
         * No Squad reference means
         * this is a normal Dashboard load.
         */
        if (!transactionRef) {
          return;
        }

        /*
         * Prevent duplicate calls under
         * React StrictMode.
         */
        verificationStartedRef.current =
          true;

        setVerificationLoading(true);

        try {
          

          /*
           * Backend receives this as:
           *
           * transaction_ref=SQPAY_xxx
           */
          const response =
            await apiEndPoints.verifySquadPayment(
              transactionRef,
            );

          /*
           * Support Axios response and
           * already-unwrapped response.
           */
          const data =
            response?.data?.data ||
            response?.data ||
            response;

         
          /*
           * Actual successful response:
           *
           * {
           *   message:
           *     "payment verified successfully",
           *   status: "success",
           *   statuscode: "00",
           *   statusmessage:
           *     "payment verified successfully"
           * }
           */
          const paymentSuccessful =
            data?.statuscode === '00' &&
            String(
              data?.status || '',
            ).toLowerCase() ===
              'success';

          if (!paymentSuccessful) {
            toast.error(
              data?.statusmessage ||
                data?.message ||
                'Payment verification failed.',
            );

            return;
          }

          toast.success(
            data?.statusmessage ||
              'Payment verified successfully.',
          );

          // ====================================================
          // CLEAR TEMPORARY PAYMENT CONTEXT
          // ====================================================

          sessionStorage.removeItem(
            'pendingSquadPaymentAmount',
          );

          sessionStorage.removeItem(
            'pendingSquadPaymentType',
          );

          sessionStorage.removeItem(
            'pendingSquadReturnPath',
          );

          // ====================================================
          // REFRESH WALLET
          // ====================================================

          /*
           * Payment verification updates
           * the wallet on the backend.
           *
           * Fetch wallet again so the
           * frontend immediately reflects
           * the new balance.
           */
          await dispatch(
            getUserVirtualAccount(),
          );

          // ====================================================
          // CLEAN CALLBACK URL
          // ====================================================

          /*
           * Change:
           *
           * /client-area?reference=SQPAY_xxx
           *
           * back to:
           *
           * /client-area
           *
           * without reloading the page.
           */
          const cleanUrl = new URL(
            window.location.href,
          );

          cleanUrl.searchParams.delete(
            'reference',
          );

          cleanUrl.searchParams.delete(
            'tref',
          );

          cleanUrl.searchParams.delete(
            'transaction_ref',
          );

          cleanUrl.searchParams.delete(
            'status',
          );

          cleanUrl.searchParams.delete(
            'message',
          );

          window.history.replaceState(
            {},
            '',
            `${cleanUrl.pathname}${cleanUrl.search}${cleanUrl.hash}`,
          );
        } catch (error) {
          console.error(
            'Squad payment verification error:',
            error,
          );

          toast.error(
            error?.response?.data
              ?.statusmessage ||
              error?.response?.data
                ?.message ||
              error?.message ||
              'Unable to verify Squad payment.',
          );
        } finally {
          setVerificationLoading(
            false,
          );
        }
      };

    verifyPendingSquadPayment();
  }, [dispatch]);

  // ============================================================
  // GENERATE SQUAD PAYMENT LINK
  // ============================================================

  const handleGenerateSquadPaymentLink =
    async () => {
      const amount =
        Number(squadAmount);

      if (
        !amount ||
        amount <= 0
      ) {
        toast.error(
          'Please enter a valid amount.',
        );

        return;
      }

      try {
        setSquadPaymentLoading(
          true,
        );

        const response =
          await apiEndPoints.generateSquadPaymentLink(
            {
              amount,
            },
          );

        /*
         * Support Axios response and
         * already-unwrapped response.
         */
        const data =
          response?.data?.data ||
          response?.data ||
          response;


        /*
         * Backend-level failure.
         */
        if (
          data?.statuscode !== '00'
        ) {
          toast.error(
            data?.statusmessage ||
              data?.message ||
              'Payment link could not be generated.',
          );

          return;
        }

        /*
         * Actual successful backend response:
         *
         * {
         *   payment_link: {
         *     data: {
         *       checkout_url:
         *         "https://pay.squadco.com/...",
         *       callback_url: "...",
         *       transaction_ref:
         *         "SQPAY_xxx",
         *       ...
         *     },
         *     message: "Success",
         *     status: 200,
         *     success: true
         *   },
         *   statuscode: "00",
         *   statusmessage: "successful"
         * }
         */

        const paymentUrl =
          data?.payment_link?.data
            ?.checkout_url;


        if (
          !paymentUrl ||
          typeof paymentUrl !==
            'string'
        ) {
          console.error(
            'Payment URL missing:',
            data,
          );

          toast.error(
            'Payment link was not returned.',
          );

          return;
        }

        // ====================================================
        // SAVE PAYMENT CONTEXT
        // ====================================================

        sessionStorage.setItem(
          'pendingSquadPaymentAmount',
          String(amount),
        );

        sessionStorage.setItem(
          'pendingSquadPaymentType',
          'wallet_funding',
        );

        /*
         * We do NOT verify here.
         *
         * User is sent to Squad.
         * After payment, backend callback
         * redirects the user to:
         *
         * /client-area?reference=SQPAY_xxx
         *
         * The verification useEffect above
         * handles the rest.
         */

        window.location.assign(
          paymentUrl,
        );
      } catch (error) {
        console.error(
          'Squad payment link generation error:',
          error,
        );

        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data
              ?.message ||
            error?.message ||
            'Unable to generate payment link.',
        );
      } finally {
        setSquadPaymentLoading(
          false,
        );
      }
    };

  // ============================================================
  // EXISTING USD PAYMENT LINK
  // ============================================================

  const dollarPaymentLink =
    useFormik({
      initialValues: {
        amount: amountValue,
      },

      validationSchema:
        Yup.object({
          amount:
            Yup.string().required(
              'Amount cannot be empty',
            ),
        }),

      onSubmit: async (
        values,
        { resetForm },
      ) => {
        try {
          const {
            amount,
          } = values;

          const {
            payload,
          } = await dispatch(
            createPaymentLink({
              amount,
            }),
          );

          if (
            payload?.statuscode !==
            '00'
          ) {
            toast.error(
              payload?.statusmessage ||
                'Unable to generate payment link.',
            );

            return;
          }

          const payment_link =
            payload?.payment_link;

          if (!payment_link) {
            toast.error(
              'Payment link was not returned.',
            );

            return;
          }

          setPaymentLink(
            payment_link,
          );

          setLinkGenerated(true);

          resetForm();

          setAmountValue('');
        } catch (error) {
          toast.error(
            error?.message ||
              'Unable to generate payment link.',
          );
        }
      },
    });

  const handleAmountChange = (
    event,
  ) => {
    const value =
      event.target.value;

    setAmountValue(value);

    dollarPaymentLink.setFieldValue(
      'amount',
      value,
    );
  };

  // ============================================================
  // FORMAT WALLET BALANCE
  // ============================================================

  const formatAmount = (
    value,
  ) => {
    const num =
      parseFloat(value);

    if (isNaN(num)) {
      return '0.00';
    }

    return num.toLocaleString(
      'en-US',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    );
  };

  // ============================================================
  // OPEN / CLOSE WALLET MODAL
  // ============================================================

  const handletoggleWalletDetails =
    () => {
      setWalletDetails(
        (previous) => !previous,
      );

      setLinkGenerated(false);

      setPaymentLink('');

      setAmountValue('');

      setSquadAmount('');

      dollarPaymentLink.resetForm();
    };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12">
      <div className="min-h-screen px-4 py-6 md:px-10 lg:col-span-12">

        {/* ================================================= */}
        {/* PROJECT / WALLET HEADER */}
        {/* ================================================= */}

        <div
          className={`grid items-start gap-4 pb-4 md:flex md:justify-between ${
            !accountName ||
            !accountNumber
              ? 'md:items-center'
              : ''
          }`}
        >
          <div className="flex items-start gap-3">

            <div className="h-fit w-fit rounded-md bg-brandBlue p-2 dark:bg-brandBlue/10">

              <img
                src={dewImage}
                alt=""
                className="h-5"
              />

            </div>

            <div className="dark:text-white">

              <div className="flex items-center gap-4">

                <p className="text-lg font-extrabold">
                  {selectedProject?.name ||
                    ''}
                </p>

                <div className="flex items-center gap-1 text-xs text-primaryBlue">

                  <Link
                    to={generatePartnerUrl(
                      '/client-area/manage',
                    )}
                  >
                    Manage project
                  </Link>

                  <span>
                    <img
                      src={gear}
                      alt=""
                      className="h-[16px]"
                    />
                  </span>

                </div>
              </div>

              <p className="mb-2 text-xs text-gray-500">
                {selectedProject?.description ||
                  ''}
              </p>

            </div>
          </div>

          <div>
            <div className="flex w-full justify-between gap-16 md:w-fit md:gap-4">

              <div className="grid text-xs dark:text-white">

                <span className="text-gray-500">
                  Wallet balance:
                </span>

                <div className="flex items-center gap-4">

                  <span className="montserrat-font !text-lg !font-extrabold !text-black dark:!text-white">

                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          currency ===
                          'NGN'
                            ? '&#x20A6;'
                            : '&#x24;',
                      }}
                    />

                    {balanceVisible
                      ? walletBalance ||
                        walletBalance ===
                          0
                        ? formatAmount(
                            walletBalance,
                          )
                        : '0.00'
                      : '***.**'}

                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setBalanceVisible(
                        (previous) =>
                          !previous,
                      )
                    }
                    className="text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-200"
                    aria-label={
                      balanceVisible
                        ? 'Hide balance'
                        : 'Show balance'
                    }
                  >
                    {balanceVisible ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>

                </div>
              </div>

              <button
                type="button"
                onClick={
                  handletoggleWalletDetails
                }
                className="w-fit rounded bg-primaryBlue p-2 text-xs text-white"
              >
                Fund Wallet
              </button>

            </div>
          </div>
        </div>

        {/* ================================================= */}
        {/* VERIFYING PAYMENT */}
        {/* ================================================= */}

        {verificationLoading && (
          <div className="mb-5 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300">
            Verifying your Squad
            payment. Please wait...
          </div>
        )}

        {/* ================================================= */}
        {/* WALLET MODAL */}
        {/* ================================================= */}

        {walletDetails && (
          <div className="fixed left-0 top-0 z-50 flex h-screen w-full items-end justify-center bg-black/70 px-4 md:items-center">

            <div className="mb-4 max-h-[80%] w-full overflow-auto rounded bg-white dark:bg-[#141D26] dark:text-white md:mb-0 md:w-[40%] lg:w-[30%]">

              {/* HEADER */}

              <div className="flex items-center justify-between border-b p-6 text-sm dark:border-b-[#c4c4c432]">

                <span className="dark:text-white">
                  {currency === 'USD'
                    ? 'Dollar'
                    : 'Naira'}{' '}
                  account details
                </span>

                <img
                  src={close}
                  onClick={
                    handletoggleWalletDetails
                  }
                  className="h-5 cursor-pointer"
                  alt="Close"
                />

              </div>

              {/* DESCRIPTION */}

              <div className="flex w-full flex-col items-start justify-start p-6">

                <span className="text-gray-700 dark:text-white">

                  {linkGenerated
                    ? 'Fund your wallet by clicking the link below.'
                    : hasPermanentVirtualAccount
                      ? 'Fund your wallet using your permanent account details.'
                      : currency ===
                          'USD'
                        ? 'Squad wallet funding is currently available for Naira payments.'
                        : 'Enter the amount you want to add to your wallet.'}

                </span>

              </div>

              <div className="grid p-6 text-sm">

                {/* ========================================= */}
                {/* PERMANENT ACCOUNT USERS */}
                {/* ========================================= */}

                {hasPermanentVirtualAccount ? (
                  linkGenerated ? (

                    <div>

                      <Input
                        type="text"
                        disabled
                        value={
                          paymentLink
                        }
                      />

                      {paymentLink && (

                        <a
                          href={
                            paymentLink
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >

                          <button
                            type="button"
                            className="mt-5 w-full rounded bg-primaryBlue p-4 text-xs text-white"
                            onClick={
                              handletoggleWalletDetails
                            }
                          >
                            Make Payment
                          </button>

                        </a>

                      )}

                    </div>

                  ) : (

                    <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2 md:gap-6">

                      {currency ===
                        'USD' && (
                        <>

                          <div className="col-span-2 grid">

                            <span className="mb-5">

                              <p className="mb-1">
                                OPTION 1:
                              </p>

                              <p>
                                Generate
                                Payment Link
                              </p>

                            </span>

                            <Input
                              type="text"
                              label="Amount"
                              placeholder="Enter amount"
                              name="amount"
                              value={
                                amountValue
                              }
                              onBlur={
                                dollarPaymentLink.handleBlur
                              }
                              onChange={
                                handleAmountChange
                              }
                              onError={
                                dollarPaymentLink
                                  .errors
                                  .amount &&
                                dollarPaymentLink
                                  .touched
                                  .amount
                                  ? dollarPaymentLink
                                      .errors
                                      .amount
                                  : null
                              }
                            />

                            <button
                              className="mt-5 w-full rounded bg-primaryBlue p-4 text-xs text-white"
                              type="button"
                              onClick={(
                                event,
                              ) => {
                                event.preventDefault();

                                dollarPaymentLink.handleSubmit(
                                  event,
                                );
                              }}
                            >
                              Generate
                              Payment Link
                            </button>

                          </div>

                          <div className="col-span-2 mt-5">

                            <p className="mb-1">
                              OPTION 2:
                            </p>

                            <span>
                              Transfer the
                              exact amount to
                              this account and
                              call{' '}

                              <a
                                href="tel:09069434255"
                                className="mx-1 text-blue-500 underline"
                              >
                                09069434255
                              </a>

                              {' '}or send a
                              payment receipt
                              to{' '}

                              <a
                                href="mailto:support@cloudvantage.com.ng"
                                className="ml-1 text-blue-500 underline"
                              >
                                support@cloudvantage.com.ng
                              </a>

                            </span>

                          </div>

                        </>
                      )}

                      <div className="grid">

                        <span>
                          Bank name:
                        </span>

                        <span className="text-gray-500">
                          {currency ===
                          'USD'
                            ? 'Guaranty Trust Bank'
                            : 'Providus Bank'}
                        </span>

                      </div>

                      <div className="grid">

                        <span>
                          Account number:
                        </span>

                        <span className="text-gray-500">
                          {currency ===
                          'USD'
                            ? '0891825462'
                            : accountNumber ||
                              ''}
                        </span>

                      </div>

                      <div className="border-b pb-5 md:col-span-2 dark:border-b-[#c4c4c432]">

                        <span>
                          Account name:
                        </span>

                        <span className="block text-gray-500">
                          {currency ===
                          'USD'
                            ? 'Cloudvantage Limited'
                            : accountName ||
                              ''}
                        </span>

                      </div>

                    </div>

                  )
                ) : currency ===
                  'USD' ? (

                  /* ========================================= */
                  /* NO PERMANENT ACCOUNT + USD */
                  /* ========================================= */

                  <div className="grid gap-4">

                    <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/20">

                      <p className="font-semibold">
                        Payment unavailable
                      </p>

                      <p className="mt-1 text-xs">
                        Squad wallet funding
                        is currently available
                        for Naira payments
                        only.
                      </p>

                    </div>

                    <p>
                      Switch your wallet
                      currency to Naira to
                      continue.
                    </p>

                  </div>

                ) : (

                  /* ========================================= */
                  /* NEW SQUAD PAYMENT LINK */
                  /* ========================================= */

                  <div className="grid gap-4">

                    <div className="rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">

                      <p className="font-semibold text-blue-800 dark:text-blue-300">
                        Pay with Squad
                      </p>

                      <p className="mt-1 text-xs text-blue-700 dark:text-blue-400">
                        Enter the amount you
                        want to add to your
                        wallet. You will be
                        redirected to
                        Squad&apos;s secure
                        payment page.
                      </p>

                    </div>

                    <Input
                      type="number"
                      label="Amount"
                      placeholder="Enter amount in Naira"
                      name="squadAmount"
                      value={
                        squadAmount
                      }
                      onChange={(
                        event,
                      ) =>
                        setSquadAmount(
                          event.target
                            .value,
                        )
                      }
                    />

                    <button
                      type="button"
                      className="w-full rounded bg-primaryBlue p-4 text-xs text-white disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={
                        squadPaymentLoading ||
                        !squadAmount ||
                        Number(
                          squadAmount,
                        ) <= 0
                      }
                      onClick={
                        handleGenerateSquadPaymentLink
                      }
                    >
                      {squadPaymentLoading
                        ? 'Generating payment link...'
                        : 'Proceed to Payment'}
                    </button>

                  </div>

                )}

                {/* ========================================= */}
                {/* CURRENCY SETTINGS */}
                {/* ========================================= */}

                <Link
                  to={generatePartnerUrl(
                    '/settings',
                  )}
                  className="mt-6 flex w-fit items-center rounded-md border border-[#c4c4c450] p-3 text-xs text-primaryBlue dark:border-[#c4c4c416]"
                >

                  <span>
                    Currency settings
                  </span>

                  <span>
                    <img
                      src={arrow}
                      alt=""
                    />
                  </span>

                </Link>

              </div>
            </div>
          </div>
        )}

        <div className="border-t border-t-[#c4c4c416] pt-6">
          <Outlet />
        </div>

      </div>
    </div>
  );
};

export default Home;